import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { safeLocalStorage } from "@/lib/safeStorage";

const STORAGE_KEY = "bb_playdates_seen_keys";
const CACHE_TTL_MS = 60_000;

// Module-level cache so the desktop + mobile nav instances share one request.
let keysCache: { keys: string[]; fetchedAt: number } | null = null;
let inflight: Promise<string[]> | null = null;

const fetchFeedKeys = async (): Promise<string[]> => {
  if (keysCache && Date.now() - keysCache.fetchedAt < CACHE_TTL_MS) {
    return keysCache.keys;
  }
  if (inflight) return inflight;
  inflight = (async () => {
    const { data, error } = await supabase.rpc("get_upcoming_meet_feed_keys");
    if (error) {
      console.error("Error fetching playdates feed keys:", error);
      return keysCache?.keys ?? [];
    }
    const keys = (data || []).filter(Boolean);
    keysCache = { keys, fetchedAt: Date.now() };
    return keys;
  })();
  try {
    return await inflight;
  } finally {
    inflight = null;
  }
};

// null = never visited /playdates on this device -> everything counts as new.
const readSeen = (): Set<string> | null => {
  const raw = safeLocalStorage.getItem(STORAGE_KEY);
  if (raw === null) return null;
  try {
    const arr = JSON.parse(raw);
    return new Set(Array.isArray(arr) ? arr.filter((k) => typeof k === "string") : []);
  } catch {
    return new Set();
  }
};

const writeSeen = (keys: string[]) => {
  const existing = readSeen() ?? new Set<string>();
  keys.forEach((k) => existing.add(k));
  // Prune keys that dropped out of the feed (past dates) to keep storage small.
  const current = new Set(keys);
  const pruned = [...existing].filter((k) => current.has(k));
  safeLocalStorage.setItem(STORAGE_KEY, JSON.stringify(pruned));
};

/**
 * Number of upcoming playdates wall items (meetups + goings) the user hasn't
 * seen yet. Clears as soon as the user lands on /playdates. Logged-in only.
 */
export function usePlaydatesBadge() {
  const { user } = useAuth();
  const location = useLocation();
  const [newCount, setNewCount] = useState(0);

  const isOnPlaydates = location.pathname === "/playdates";

  useEffect(() => {
    if (!user) {
      setNewCount(0);
      return;
    }
    let cancelled = false;
    fetchFeedKeys().then((keys) => {
      if (cancelled) return;
      if (isOnPlaydates) {
        writeSeen(keys);
        setNewCount(0);
      } else {
        const seen = readSeen();
        setNewCount(seen === null ? keys.length : keys.filter((k) => !seen.has(k)).length);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [user, isOnPlaydates]);

  return newCount;
}
